import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { CheckCircle2, ChevronDown, ChevronUp, LifeBuoy } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { CharCounter } from '@/components/shared/CharCounter';
import { EmptyState } from '@/components/shared/EmptyState';
import { ErrorState } from '@/components/shared/ErrorState';
import { SupportTicketStatusBadge } from '@/components/shared/SupportTicketStatusBadge';
import { formatDate, formatDateTime } from '@/lib/formatDate';
import {
  supportService,
  SUPPORT_DESCRIPTION_MAX,
  type SupportTicket,
  type SupportTicketReason,
} from '@/services/supportService';

const REASON_VALUES = [
  'question',
  'problem',
  'suggestion',
  'other',
] as const satisfies readonly SupportTicketReason[];

// Mirrors the backend's 1..1000 bound on ``support_tickets.description``
// (defense-in-depth alongside the Textarea's hard ``maxLength``).
const ticketFormSchema = z.object({
  reason: z.enum(REASON_VALUES),
  description: z
    .string()
    .min(1, { message: 'support.form.description_required' })
    .max(SUPPORT_DESCRIPTION_MAX, { message: 'support.form.description_too_long' }),
});

type TicketFormValues = z.infer<typeof ticketFormSchema>;

const DEFAULT_VALUES: TicketFormValues = { reason: 'question', description: '' };

interface SupportPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * F161 FR3 — right-side support panel: new-ticket form on top, the tenant's
 * ticket history below. Opened from `SupportMenu`'s "Falar com o suporte"
 * dropdown item; replaces the old `mailto:` placeholder entirely (FR5).
 *
 * `context.route` is auto-captured from the current path via `useLocation`
 * (not `window.location`, for consistency with the rest of the app) and
 * sent invisibly on create — no UI surfaces it.
 */
export function SupportPanel({ open, onOpenChange }: SupportPanelProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const [tickets, setTickets] = useState<SupportTicket[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [createNotice, setCreateNotice] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  // F162 FR4 — a ticket expands in place to reveal its thread (description
  // bubble + sent replies). Not persisted across panel reopen — see the
  // open-transition reset effect below.
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const form = useForm<TicketFormValues>({
    resolver: zodResolver(ticketFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const fetchTickets = async () => {
    setLoadError(false);
    setTickets(null);
    try {
      const result = await supportService.list();
      setTickets(result);
    } catch {
      setLoadError(true);
    }
  };

  // AT#5 — "Sheet closes/reopens preserving nothing stale": every open
  // transition resets the form + success banner and refetches history, so a
  // reopened panel never shows a previous session's leftovers.
  useEffect(() => {
    if (open) {
      form.reset(DEFAULT_VALUES);
      setCreateNotice(false);
      setApiError(null);
      setExpandedId(null);
      void fetchTickets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onSubmit = async (values: TicketFormValues) => {
    setApiError(null);
    setCreateNotice(false);
    try {
      const created = await supportService.create({
        reason: values.reason,
        description: values.description,
        context: { route: location.pathname },
      });
      // Optimistic prepend (FR3) — the create response already has everything
      // the history card needs, so no extra GET round-trip.
      setTickets((prev) => [created, ...(prev ?? [])]);
      setCreateNotice(true);
      form.reset(DEFAULT_VALUES);
    } catch {
      setApiError(t('common.error_generic'));
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-6 overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{t('support.panel.title')}</SheetTitle>
          <SheetDescription>{t('support.panel.subtitle')}</SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('support.form.reason_label')}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="min-h-11" data-testid="support-reason-select">
                        <SelectValue placeholder={t('support.form.reason_placeholder')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {REASON_VALUES.map((value) => (
                        <SelectItem key={value} value={value}>
                          {t(`support.reason.${value}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('support.form.description_label')}</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder={t('support.form.description_placeholder')}
                      maxLength={SUPPORT_DESCRIPTION_MAX}
                      rows={4}
                    />
                  </FormControl>
                  <CharCounter value={field.value} max={SUPPORT_DESCRIPTION_MAX} mode="always" />
                  <FormMessage />
                </FormItem>
              )}
            />

            {apiError ? (
              <p role="alert" className="text-sm text-destructive">
                {apiError}
              </p>
            ) : null}

            <Button
              type="submit"
              data-testid="support-form-submit"
              disabled={form.formState.isSubmitting}
              className="min-h-11"
            >
              {form.formState.isSubmitting
                ? t('support.form.submitting')
                : t('support.form.submit')}
            </Button>
          </form>
        </Form>

        {createNotice ? (
          <div
            role="status"
            data-testid="support-create-notice"
            className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm"
          >
            <CheckCircle2 className="h-4 w-4 shrink-0 text-success" aria-hidden="true" />
            <span>{t('support.form.created_notice')}</span>
          </div>
        ) : null}

        <div className="flex flex-col gap-3 border-t pt-4">
          <h3 className="text-sm font-semibold">{t('support.history.title')}</h3>

          {tickets === null && !loadError ? (
            <div className="flex flex-col gap-2" data-testid="support-history-skeleton">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : loadError ? (
            <ErrorState
              variant="load-failed"
              title={t('support.history.load_error_title')}
              body={t('support.history.load_error_body')}
              cta={
                <Button type="button" variant="outline" onClick={() => void fetchTickets()}>
                  {t('common.retry')}
                </Button>
              }
            />
          ) : tickets && tickets.length === 0 ? (
            <EmptyState
              icon={LifeBuoy}
              title={t('support.history.empty_title')}
              subtitle={t('support.history.empty_subtitle')}
            />
          ) : (
            <ul className="flex flex-col gap-3">
              {tickets?.map((ticket) => {
                const isExpanded = expandedId === ticket.id;
                const messages = ticket.messages ?? [];
                return (
                  <li
                    key={ticket.id}
                    data-testid={`support-ticket-${ticket.id}`}
                    className="rounded-lg border p-3"
                  >
                    <button
                      type="button"
                      data-testid={`support-ticket-toggle-${ticket.id}`}
                      aria-expanded={isExpanded}
                      className="flex min-h-11 w-full flex-col gap-1 text-left"
                      onClick={() => setExpandedId(isExpanded ? null : ticket.id)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <span className="min-w-0 truncate text-sm font-medium">
                          {t(`support.reason.${ticket.reason}`)}
                        </span>
                        <div className="flex shrink-0 items-center gap-1.5">
                          <SupportTicketStatusBadge status={ticket.status} />
                          {isExpanded ? (
                            <ChevronUp
                              className="h-4 w-4 text-muted-foreground"
                              aria-hidden="true"
                            />
                          ) : (
                            <ChevronDown
                              className="h-4 w-4 text-muted-foreground"
                              aria-hidden="true"
                            />
                          )}
                        </div>
                      </div>
                      <p className="line-clamp-2 break-words text-sm text-muted-foreground">
                        {ticket.description}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(ticket.created_at)}
                      </p>
                    </button>

                    {isExpanded ? (
                      <div
                        className="mt-3 flex flex-col gap-2 border-t pt-3"
                        data-testid={`support-ticket-thread-${ticket.id}`}
                      >
                        {/* Original description — first bubble, straight from the
                            ticket row. Never mirrored into `messages` (F162 note). */}
                        <div className="flex flex-col gap-1 rounded-lg border bg-muted px-3 py-2">
                          <span className="text-xs font-medium text-muted-foreground">
                            {t('support.thread.original_message')}
                          </span>
                          <p className="whitespace-pre-wrap break-words text-sm">
                            {ticket.description}
                          </p>
                        </div>

                        {messages.length === 0 ? (
                          <p className="text-xs text-muted-foreground">
                            {t('support.thread.no_replies')}
                          </p>
                        ) : (
                          messages.map((message) => (
                            <div
                              key={message.id}
                              data-testid={`support-message-${message.id}`}
                              className="ml-4 flex flex-col gap-1 rounded-lg border bg-card px-3 py-2"
                            >
                              <span className="text-xs font-medium text-muted-foreground">
                                {t('support.thread.reply_from_support')}
                              </span>
                              <p className="whitespace-pre-wrap break-words text-sm">
                                {message.body}
                              </p>
                              <span className="text-xs text-muted-foreground">
                                {formatDateTime(message.sent_at ?? message.created_at)}
                              </span>
                            </div>
                          ))
                        )}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
